// src/constants/general.constant.ts
var Stage = /* @__PURE__ */ ((Stage4) => {
  Stage4["stg"] = "stg";
  Stage4["uat"] = "uat";
  Stage4["prod"] = "prod";
  Stage4["staging"] = "staging";
  Stage4["production"] = "production";
  return Stage4;
})(Stage || {});
var BaseURIS = {
  ["stg" /* stg */]: "https://gw-staging.efishery.com/auth/v2/",
  ["staging" /* staging */]: "https://gw-staging.efishery.com/auth/v2/",
  ["uat" /* uat */]: "https://gw-uat.efishery.com/auth/v2/",
  ["prod" /* prod */]: "https://gw.efishery.com/auth/v2/",
  ["production" /* production */]: "https://gw.efishery.com/auth/v2/"
};
var KEYS = {
  LOGGED_FLAG_KEY: "{{stage}}_ef_sign",
  ACCESS_TOKEN_KEY: "{{stage}}_ef_token",
  REFRESH_TOKEN_KEY: "{{stage}}_ef_refresh_token",
  USER_PROFILE_KEY: "{{stage}}_ef_user",
  VERIFIER_KEY: "ef_{{stage}}_verifier"
};

// src/libs/persistence.ts
var genPreparedKey = (key, stage) => {
  return key.replace(
    stage === "prod" /* prod */ ? "{{stage}}_" : "{{stage}}",
    stage === "prod" /* prod */ ? "" : stage
  );
};
var getItem = (key, stage) => {
  if (!window.localStorage) {
    throw Error("window.localStorage is undefined please do this action at the browser");
  }
  return window.localStorage.getItem(genPreparedKey(key, stage));
};
var setItem = (key, value, stage) => {
  if (!window.localStorage) {
    throw Error("window.localStorage is undefined please do this action at the browser");
  }
  window.localStorage.setItem(genPreparedKey(key, stage), value);
};
var deleteItem = (key, stage) => {
  if (!window.localStorage) {
    throw Error("window.localStorage is undefined please do this action at the browser");
  }
  window.localStorage.removeItem(genPreparedKey(key, stage));
};
var setCookie = (key, value, minutes, explicitUnsecure, stage) => {
  if (typeof document === "undefined") {
    throw new Error("document is undefined");
  }
  const currentDomain = window.location.hostname.replace(/.*?([^.]+\.[^.]+)$/, "$1");
  const escapedKey = encodeURIComponent(genPreparedKey(key, stage)).replace(/%(2[346B]|5E|60|7C)/g, decodeURIComponent).replace(/[()]/g, window.escape);
  let cookie = `${escapedKey}=${value};`;
  if (minutes !== null) {
    const date = /* @__PURE__ */ new Date();
    date.setTime(date.getTime() + minutes * 60 * 1e3);
    cookie += ` expires=${date.toUTCString()};`;
  }
  cookie += " path=/;";
  if (stage !== "stg" /* stg */ && !explicitUnsecure) {
    cookie += " Secure;";
  }
  cookie += ` domain=${currentDomain};`;
  document.cookie = cookie;
};
var getCookie = (key, stage) => {
  if (typeof document === "undefined") {
    throw new Error("document is undefined");
  }
  const preparedKey = genPreparedKey(key, stage);
  function readVal(value) {
    if (value[0] === '"') {
      value = value.slice(1, -1);
    }
    return value.replace(/(%[\dA-F]{2})+/gi, decodeURIComponent);
  }
  const cookies = document.cookie ? document.cookie.split("; ") : [];
  let cookieValue = null;
  for (let i = 0; i < cookies.length; i++) {
    const parts = cookies[i].split("=");
    const value = parts.slice(1).join("=");
    const found = decodeURIComponent(parts[0]);
    if (preparedKey === found) {
      cookieValue = readVal(value);
      break;
    }
  }
  return cookieValue;
};
var deleteCookie = (key, stage) => {
  return setCookie(key, "", -1, true, stage);
};

// src/libs/verifier.ts
var generateVerifier = () => {
  return Math.random().toString(16).substring(2, 16) + "efishery" + Math.random().toString(16).substring(2, 16);
};
var initVerifier = (stage) => {
  let publicVerifier = "";
  const hasVerifier = Boolean(getCookie(KEYS.VERIFIER_KEY, stage));
  if (hasVerifier) {
    publicVerifier = getCookie(KEYS.VERIFIER_KEY, stage);
  } else {
    const verifier = generateVerifier();
    setCookie(KEYS.VERIFIER_KEY, verifier, null, false, stage);
    publicVerifier = verifier;
  }
  return publicVerifier;
};
var pruneVerifier = (stage) => {
  deleteCookie(KEYS.VERIFIER_KEY, stage);
};

// src/libs/encrypt.ts
import { TextEncoder } from "web-encoding";
var sha256 = async (plainText) => {
  const encoder = new TextEncoder();
  const data = encoder.encode(plainText);
  return window.crypto.subtle.digest("SHA-256", data);
};
var base64urlencode = (buff) => {
  let str = "";
  const bytes = new Uint8Array(buff);
  const len = bytes.byteLength;
  for (let i = 0; i < len; i++) {
    str += String.fromCharCode(bytes[i]);
  }
  return window.btoa(str).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
};

// src/libs/httpClient.ts
import axios from "axios";
var createHttpRequestClient = (stage) => {
  axios.defaults.baseURL = BaseURIS[stage];
  return axios.create({
    baseURL: BaseURIS[stage],
    withCredentials: true
  });
};

// src/modules/auth/services/general.service.ts
import Cookie from "js-cookie";

// src/libs/header.ts
var addStagingAditionalAuthorizationHeader = (accessToken, stage) => ({
  Authorization: stage === "stg" /* stg */ ? `Bearer ${accessToken}` : ""
});
var addExplicitAuthorizationHeader = (accessToken) => accessToken ? {
  Authorization: `Bearer ${accessToken}`
} : {};

// src/modules/auth/services/general.service.ts
var GeneralService = class {
  _httpClient;
  _stage;
  _userProfileKey;
  _appInfoKey;
  constructor(httpClient, userProfileKey, stage, appInfoKey) {
    this._httpClient = httpClient;
    this._stage = stage;
    this._userProfileKey = userProfileKey;
    this._appInfoKey = appInfoKey;
  }
  async doExchange(payload) {
    return this._httpClient.post("auth/login", payload).then((r) => ({
      accessToken: r.data.data.access_token,
      refreshToken: r.data.data.refresh_token,
      expiredAccessInMinute: r.data.data.expired_access_in_minute,
      expiredRefreshInMinute: r.data.data.expired_refresh_in_minute
    }));
  }
  async doRefresh(payload) {
    return this._httpClient.post("auth/refresh", payload).then((r) => ({
      accessToken: r.data.data.access_token,
      refreshToken: r.data.data.refresh_token,
      expiredAccessInMinute: r.data.data.expired_access_in_minute,
      expiredRefreshInMinute: r.data.data.expired_refresh_in_minute
    }));
  }
  async getUserProfile(accessToken, domainCode) {
    return this._httpClient.get(
      `user/profile${domainCode ? `?domain=${domainCode}` : ""}`,
      {
        headers: {
          ...addStagingAditionalAuthorizationHeader(accessToken, this._stage),
          ...addExplicitAuthorizationHeader(accessToken)
        }
      }
    ).then((r) => {
      setItem(this._userProfileKey, window.btoa(JSON.stringify(r.data.data)), this._stage);
      return r.data;
    });
  }
  getUserProfileFromBrowser() {
    const userProfileResponse = {
      success: true,
      message: "",
      error: "",
      data: null
    };
    const userProfileFromBrowser = getItem(this._userProfileKey, this._stage);
    if (userProfileFromBrowser) {
      userProfileResponse.data = JSON.parse(window.atob(userProfileFromBrowser));
    }
    return userProfileResponse;
  }
  async doLogout(accessToken) {
    return this._httpClient.get("auth/logout", {
      headers: {
        ...addStagingAditionalAuthorizationHeader(accessToken, this._stage)
      }
    }).then((r) => r.data);
  }
  async getCookieKeys(clientId) {
    const infoKey = genPreparedKey(this._appInfoKey, this._stage);
    const cachedAppInfo = Cookie.get(genPreparedKey(this._appInfoKey, this._stage));
    if (cachedAppInfo) {
      return JSON.parse(Buffer.from(cachedAppInfo, "base64").toString("utf-8"));
    }
    const fallbackAccessTokenKey = genPreparedKey(KEYS.ACCESS_TOKEN_KEY, this._stage);
    const fallbackRefreshTokenKey = genPreparedKey(KEYS.REFRESH_TOKEN_KEY, this._stage);
    const fallbackTokenKey = {
      accessTokenKey: fallbackAccessTokenKey,
      refreshTokenKey: fallbackRefreshTokenKey
    };
    return this._httpClient.get(`auth/info/${clientId}`).then((r) => {
      var _a, _b;
      if (((_a = r.data.data) == null ? void 0 : _a.refresh_token_cookie_key) && ((_b = r.data.data) == null ? void 0 : _b.access_token_cookie_key)) {
        Cookie.set(
          infoKey,
          Buffer.from(
            JSON.stringify({
              accessTokenKey: r.data.data.access_token_cookie_key,
              refreshTokenKey: r.data.data.refresh_token_cookie_key
            }),
            "utf-8"
          ).toString("base64"),
          { path: "/" }
        );
        return {
          accessTokenKey: r.data.data.access_token_cookie_key,
          refreshTokenKey: r.data.data.refresh_token_cookie_key
        };
      }
      return fallbackTokenKey;
    }).catch((err) => {
      throw new Error(err);
    });
  }
};

// src/modules/auth/index.ts
var { LOGGED_FLAG_KEY, ACCESS_TOKEN_KEY, REFRESH_TOKEN_KEY } = KEYS;
var Auth = class {
  /**
   * Identity for consumer application.
   * @internal
   */
  _clientId;
  /**
   * It refer into stage of auth service, you can choose stg | uat | prod
   * @internal
   * @defaultValue `Stage.stg`
   */
  _stage = "stg" /* stg */;
  /**
   * Verifier code, generate code challenge or exchange auth code, you need this one.
   * @internal
   */
  _verifier = "";
  refreshing = null;
  /**
   * GeneralService Instance
   * @internal
   */
  generalService;
  /**
   * Logged Flag Key
   * @internal
   */
  _loggedFlagKey = LOGGED_FLAG_KEY;
  /**
   * Access Token Key
   * @internal
   */
  _accessTokenKey = ACCESS_TOKEN_KEY;
  /**
   * Refresh Token Key
   * @internal
   */
  _refreshTokenKey = REFRESH_TOKEN_KEY;
  /**
   * Persistence User Profile Key
   * @internal
   */
  _userProfileKey = "{{stage}}_ef_user";
  /**
   * Persistence App Info Key
   * @internal
   */
  _appInfoKey = "{{stage}}_ef_app_info";
  /**
   * Is cookie keys enabled and initialized
   * @internal
   */
  _isCookieKeysEnabled = false;
  /**
   * Cookie keys storage
   * @internal
   */
  _cookieKeys = null;
  constructor(clientId, stage, opts) {
    const transformedStage = this.stageTransform(stage);
    this._clientId = clientId;
    this._stage = transformedStage;
    this.generalService = new GeneralService(
      createHttpRequestClient(this._stage),
      this._userProfileKey,
      this._stage,
      this._appInfoKey
    );
    this._isCookieKeysEnabled = Boolean(opts == null ? void 0 : opts.enableCookieKeys);
    if (this._isCookieKeysEnabled && !this._cookieKeys) {
      this.initializeCookieKeys().then((keys) => {
        this._cookieKeys = keys;
      });
    }
  }
  async initializeCookieKeys() {
    return await this.generalService.getCookieKeys(this._clientId);
  }
  stageTransform(stage) {
    switch (stage) {
      case "production" /* production */:
        return "prod" /* prod */;
      case "staging" /* staging */:
        return "stg" /* stg */;
      default:
        return stage;
    }
  }
  makeSureVerifierGenerated() {
    if (!this._verifier) {
      this._verifier = initVerifier(this._stage);
    }
  }
  /**
   * Returns hashing result from saved code verifier
   *
   * @returns {string} code challenge
   */
  async generateChallengeCode() {
    this.makeSureVerifierGenerated();
    const hashed = await sha256(this._verifier);
    return base64urlencode(hashed);
  }
  /**
   * To validate your access into authorization code, so if it passed, will be returns access and refresh token.
   *
   * @param {string} authorizationCode authorization code from callback (code)
   * @param {string} grantType grantType from callback (grant_type)
   * @returns `accessToken` and `refreshToken`
   */
  async exchange(authorizationCode, grantType) {
    this.makeSureVerifierGenerated();
    return this.generalService.doExchange({
      client_id: this._clientId,
      code: authorizationCode,
      grant_type: grantType,
      verifier: this._stage === "stg" /* stg */ ? this._verifier : ""
    }).then((response) => {
      pruneVerifier(this._stage);
      return response;
    });
  }
  /**
   * If your token is invalid, use this function to refresh it.
   *
   * @param {string} refreshToken refreshToken from your persist state
   * @returns `accessToken` and `refreshToken`
   */
  async refresh(refreshToken) {
    if (this.refreshing) {
      return this.refreshing;
    }
    this.refreshing = this.generalService.doRefresh({
      client_id: this._clientId,
      refresh_token: refreshToken
    }).finally(() => {
      setTimeout(() => {
        this.refreshing = null;
      }, 700);
    });
    return this.refreshing;
  }
  /**
   * Validate and automatically save accessToken and refreshToken into sessionStorage.
   *
   * @param {string} authorizationCode authorization code from callback
   * @param {string} grantType grantType code from callback
   * @returns `accessToken` and `refreshToken`
   */
  async persistExchange(authorizationCode, grantType) {
    this.makeSureVerifierGenerated();
    return this.exchange(authorizationCode, grantType).then(async (response) => {
      const accessTokenKey = await this.getAccessTokenKey();
      const refreshTokenKey = await this.getRefreshTokenKey();
      setCookie(
        accessTokenKey,
        response.accessToken,
        response.expiredAccessInMinute,
        true,
        this._stage
      );
      setCookie(
        refreshTokenKey,
        response.refreshToken,
        response.expiredRefreshInMinute,
        true,
        this._stage
      );
      setCookie(
        this._accessTokenKey,
        response.accessToken,
        response.expiredAccessInMinute,
        true,
        this._stage
      );
      return response;
    });
  }
  /**
   * When you exchange authorization with persistExchange, we recommend using this API to refresh persistence accessToken and refreshToken.
   *
   * @returns `accessToken` and `refreshToken`
   */
  async persistRefresh() {
    deleteItem(this._loggedFlagKey, this._stage);
    const accessTokenKey = await this.getAccessTokenKey();
    const refreshTokenKey = await this.getRefreshTokenKey();
    const refreshToken = getCookie(refreshTokenKey, this._stage);
    return this.refresh(refreshToken).then((response) => {
      setCookie(
        accessTokenKey,
        response.accessToken,
        response.expiredAccessInMinute,
        true,
        this._stage
      );
      setCookie(
        refreshTokenKey,
        response.refreshToken,
        response.expiredRefreshInMinute,
        true,
        this._stage
      );
      setCookie(
        this._accessTokenKey,
        response.accessToken,
        response.expiredAccessInMinute,
        true,
        this._stage
      );
      return response;
    });
  }
  /**
   * Retrieve user profile from server or local (TTL 24 hours)
   *
   * @param {string} accessToken (optional) to fetch user profile with explicit accessToken
   * @returns {Promise<UserProfileResponse>}
   */
  async getProfile(accessToken) {
    const userProfile = this.generalService.getUserProfileFromBrowser();
    if (userProfile.data && Object.values(userProfile.data).length > 0) {
      return Promise.resolve(userProfile);
    }
    return await this.generalService.getUserProfile(accessToken);
  }
  /**
   * Retrieve accessToken from cookie
   *
   * @returns `Promise<accessToken>`
   */
  async getAccessToken() {
    const accessTokenKey = await this.getAccessTokenKey();
    return getCookie(accessTokenKey, this._stage);
  }
  /**
   * Retrieve accessTokenKey from cookie
   *
   * @returns Promise<string>
   */
  async getAccessTokenKey() {
    if (this._isCookieKeysEnabled) {
      if (!this._cookieKeys) {
        return this.initializeCookieKeys().then((keys) => {
          this._cookieKeys = keys;
          return Promise.resolve(keys.accessTokenKey);
        });
      } else {
        return Promise.resolve(this._cookieKeys.accessTokenKey);
      }
    } else {
      return Promise.resolve(genPreparedKey(this._accessTokenKey, this._stage));
    }
  }
  /**
   * Retrieve refreshToken from cookie
   *
   * @returns `refreshToken`
   */
  async getRefreshToken() {
    const refreshTokenKey = await this.getRefreshTokenKey();
    return getCookie(refreshTokenKey, this._stage);
  }
  /**
   * Retrieve refreshTokenKey from cookie
   *
   * @returns Promise<string>
   */
  async getRefreshTokenKey() {
    if (this._isCookieKeysEnabled) {
      if (!this._cookieKeys) {
        return this.initializeCookieKeys().then((keys) => {
          this._cookieKeys = keys;
          return Promise.resolve(keys.refreshTokenKey);
        });
      } else {
        return Promise.resolve(this._cookieKeys.refreshTokenKey);
      }
    }
    return Promise.resolve(genPreparedKey(this._refreshTokenKey, this._stage));
  }
  /**
   * Check signed as logged user (we can't check the user cookie, so this way to check logged user before we really check with API getProfile)
   *
   * @returns {boolean}
   */
  async isSignedAsLogged() {
    const accessTokenKey = await this.getAccessTokenKey();
    return Boolean(getCookie(accessTokenKey, this._stage));
  }
  /**
   * Refresh user profile immediately
   *
   * @returns {Promise<UserProfileResponse>}
   */
  async hardRefreshProfile() {
    const accessToken = await this.getAccessToken();
    return this.generalService.getUserProfile(String(accessToken));
  }
  /**
   * Revoke access to refresh new token
   *
   * @returns `BasicResponse`
   */
  async logout() {
    const accessToken = await this.getAccessToken();
    return this.generalService.doLogout(String(accessToken)).finally(async () => {
      deleteItem(this._userProfileKey, this._stage);
      const accessTokenKey = await this.getAccessTokenKey();
      const refreshTokenKey = await this.getRefreshTokenKey();
      deleteCookie(accessTokenKey, this._stage);
      deleteCookie(refreshTokenKey, this._stage);
      deleteCookie(genPreparedKey(this._appInfoKey, this._stage), this._stage);
    });
  }
};

// src/modules/imageProxy/imageServiceVendor.ts
var baseUrls = {
  ["stg" /* stg */]: "https://gw-staging.efishery.com/fms/v2",
  ["staging" /* staging */]: "https://gw-staging.efishery.com/fms/v2",
  ["uat" /* uat */]: "https://gw-uat.efishery.com/fms/v2",
  ["prod" /* prod */]: "https://gw.efishery.com/fms/v2",
  ["production" /* production */]: "https://gw.efishery.com/fms/v2"
};
function factory(initCfg) {
  return (target, cfg) => {
    const proxyCfg = [];
    if (cfg.quality) {
      proxyCfg.push(`q${cfg.quality}`);
    }
    if (cfg.width) {
      proxyCfg.push(`w${cfg.width}`);
    }
    if (cfg.height) {
      proxyCfg.push(`h${cfg.height}`);
    }
    return `${baseUrls[initCfg.env]}/p/${proxyCfg.join(";")}/${target}`;
  };
}

// src/modules/imageProxy/proxy.ts
var ImageProxyVendor = /* @__PURE__ */ ((ImageProxyVendor2) => {
  ImageProxyVendor2["ImageService"] = "image-service";
  return ImageProxyVendor2;
})(ImageProxyVendor || {});
function createImageProxy(initCfg) {
  const vendors = /* @__PURE__ */ new Map();
  vendors.set("image-service" /* ImageService */, factory(initCfg));
  return (target, cfg) => {
    const vendor = !cfg.vendor ? vendors.get(initCfg.vendor) : vendors.get(cfg.vendor);
    if (!vendor) {
      throw new Error(`Invalid vendor ${!cfg.vendor ? initCfg.vendor : cfg.vendor}`);
    }
    return vendor(target, cfg);
  };
}
export {
  Auth,
  BaseURIS,
  ImageProxyVendor,
  Stage,
  createImageProxy
};
